// App.tsx
import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, Snackbar, Alert } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardLayout from './layouts/DashboardLayout';

// Public pages
import LandingPage from './pages/public/LandingPage';
import LoginPage from './pages/public/LoginPage';
import ForgotPasswordPage from './pages/public/ForgotPasswordPage';
import AboutPage from './pages/public/AboutPage';
import ContactPage from './pages/public/ContactPage';

// Dashboard pages
import DashboardPage from './pages/dashboard/DashboardPage';
import ChangePassword from './pages/dashboard/ChangePassword';
// Configuration pages
import ProductCategoriesPage from './pages/configuration/ProductCategoriesPage';
import ProductsPage from './pages/configuration/ProductsPage';
import SellerInformationPage from './pages/configuration/SellerInformationPage';
import CostCategoriesPage from './pages/configuration/CostCategoriesPage';


import ManageStaffs from './pages/ManageStaffs';

// Entry point pages
import PurchaseInfoPage from './pages/entry-form/PurchaseInfoPage';
import SellingInfoPage from './pages/entry-form/SellingInfoPage';
import ReturnInfoPage from './pages/entry-form/ReturnInfoPage';
import CostInfoPage from './pages/entry-form/CostInfoPage';
import RoiInfoPage from './pages/entry-form/RoiInfoPage';

// Report pages
import StockInfoReport from './pages/reports/StockInfoReport';
import PurchaseInfoReport from './pages/reports/PurchaseInfoReport';
import SellingInfoReport from './pages/reports/SellingInfoReport';
import ReturnInfoReport from './pages/reports/ReturnInfoReport';
import CostInfoReport from './pages/reports/CostInfoReport';
import RoiInfoReport from './pages/reports/RoiInfoReport';
import AuditInfoReport from './pages/reports/AuditInfoReport';
import PivotInfoReport from './pages/reports/PivotInfoReport';
import DueInfoReport from './pages/reports/DueInfoReport';
import AnalyticsPage from './pages/analytics/AnalyticsPage';
import InvoiceInfoPage from './pages/invoices/InvoiceInfoPage';


import { Box, Typography } from '@mui/material';

// API event bus (for global toasts & redirects)
import { apiEvents } from './services/api';

// ── MUI Theme ────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976D2',
      dark: '#1565C0',
      light: '#42A5F5',
    },
    secondary: {
      main: '#7B1FA2',
      dark: '#6A1B9A',
    },
    background: {
      default: '#f4f6f9',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
  },
});

// ── Toast manager (listens to apiEvents) ─────────────────────────────────────
const ToastManager: React.FC = () => {
  const navigate = useNavigate();
  const [toast, setToast] = React.useState<{ message: string; severity: 'success' | 'error' | 'info' } | null>(null);

  useEffect(() => {
    const onSuccess = (msg: string) => setToast({ message: msg, severity: 'success' });
    const onError   = (msg: string) => setToast({ message: msg, severity: 'error' });
    const onInfo    = (msg: string) => setToast({ message: msg, severity: 'info' });
    const onNav     = (path: string) => navigate(path, { replace: true });
    const onUnauthorized = () => navigate('/login', { replace: true });

    apiEvents.on('toast:success', onSuccess);
    apiEvents.on('toast:error',   onError);
    apiEvents.on('toast:info',    onInfo);
    apiEvents.on('navigate',      onNav);
    apiEvents.on('auth:unauthorized', onUnauthorized);   

    return () => {
      apiEvents.off('toast:success', onSuccess);
      apiEvents.off('toast:error',   onError);
      apiEvents.off('toast:info',    onInfo);
      apiEvents.off('navigate',      onNav);
      apiEvents.off('auth:unauthorized', onUnauthorized); 
    };
  }, [navigate]);

  return (
    <Snackbar
      open={!!toast}
      autoHideDuration={4000}
      onClose={() => setToast(null)}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      {toast ? (
        <Alert
          onClose={() => setToast(null)}
          severity={toast.severity}
          variant="filled"
          sx={{ borderRadius: 3, fontWeight: 600 }}
        >
          {toast.message}
        </Alert>
      ) : <span />}
    </Snackbar>
  );
};

// ── App ───────────────────────────────────────────────────────────────────────
const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <ToastManager />
          <Routes>
            {/* ── Public routes ── */}
            <Route path="/"               element={<LandingPage />} />
            <Route path="/login"          element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            {/* <Route path="/about"          element={<AboutPage />} />
            <Route path="/contact"        element={<ContactPage />} /> */}

            {/* ── Protected dashboard routes ── */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <DashboardPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* Entry Form with child routes */}
            <Route
              path="/entry-form/purchase-info"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <PurchaseInfoPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/entry-form/selling-info"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <SellingInfoPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/entry-form/return-info"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ReturnInfoPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/entry-form/cost-info"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <CostInfoPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/entry-form/roi-info"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <RoiInfoPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/stock-info"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <StockInfoReport />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/puchase-info"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <PurchaseInfoReport />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/selling-info"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <SellingInfoReport />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/return-info"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ReturnInfoReport />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/cost-info"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <CostInfoReport />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/roi-info"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <RoiInfoReport />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/pivot-info"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <PivotInfoReport />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/due-info"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <DueInfoReport />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/audit-info"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <AuditInfoReport />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoices/invoice-info"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <InvoiceInfoPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/configuration/product-categories"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ProductCategoriesPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/configuration/products"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ProductsPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/configuration/seller-information"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <SellerInformationPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/configuration/cost-categories"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <CostCategoriesPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/manage-staffs"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ManageStaffs />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <AnalyticsPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            /> 
            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ChangePassword />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            /> 
            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
};

// ── Temporary placeholder for unbuilt pages ───────────────────────────────────
const PlaceholderPage: React.FC<{ title: string }> = ({ title }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      gap: 2,
    }}
  >
    <Typography
      variant="h4"
      sx={{
        fontWeight: 700,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
      }}
    >
      {title}
    </Typography>
    <Typography variant="body1" color="text.secondary">
      This page is under construction.
    </Typography>
  </Box>
);

export default App;